import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../providers/TranslationProvider';
import { SCREENS } from '../utils/constants';
import { Moon, Sun, Settings, X, Apple } from 'lucide-react';
import { getCurrentLanguage, triggerLanguageChange, SUPPORTED_LANGUAGES } from '../i18n';
import useIsMobile from '../hooks/useIsMobile';
import DecryptedText from '../components/text/DecryptedText';
import TypingText from '../components/text/TypingText';
import Logo from '../icons/logo.svg';

const WelcomeScreen = () => {
    const { navigateToScreen, isDarkMode, toggleTheme } = useUI();
    const { isAuthenticated, loading } = useAuth();
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    // Preferences Panel State
    const [showPreferences, setShowPreferences] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(() => getCurrentLanguage());

    // Slogans
    const slogans = useMemo(() => [
        t('slogan.decentralized', "Dünya'nın ilk merkeziyetsiz kimlik çözümü"),
        t('slogan.evolution', "Kimliğin evriminde bir sonraki adım"),
        t('slogan.control', "Verilerinizin kontrolünü geri alın"),
        t('slogan.secure', "Dijital kimliğinizin güvenli kalesi"),
    ], [t]);

    // Auto-redirect if authenticated
    useEffect(() => {
        if (!loading && isAuthenticated) {
            const timer = setTimeout(() => navigateToScreen(SCREENS.DASHBOARD), 500);
            return () => clearTimeout(timer);
        }
    }, [isAuthenticated, loading, navigateToScreen]);

    // Animation Variants
    const containerVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.8, ease: "easeOut" } },
        exit: { opacity: 0, scale: 0.95, transition: { duration: 0.5 } }
    };

    const logoGlowVariants = {
        animate: {
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.1, 1],
            transition: {
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
            }
        }
    };

    // Transition State
    const [isExiting, setIsExiting] = useState(false);

    const handleGetStarted = () => {
        setIsExiting(true);
        setTimeout(() => {
            navigateToScreen(SCREENS.SIGNUP);
        }, 800); // Wait for animation
    };

    return (
        <motion.div
            className="w-full min-h-screen flex flex-col items-center justify-center bg-transparent relative"
            variants={containerVariants}
            initial="initial"
            animate="animate"
            exit="exit"
        >


            {/* Top Right Controls (Absolute) */}
            <motion.div
                className="absolute top-6 right-6 z-30 flex gap-4"
                animate={{ opacity: isExiting ? 0 : 1 }}
                transition={{ duration: 0.3 }}
            >
                <motion.button
                    whileHover={{ scale: 1.1, rotate: 15 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleTheme}
                    className="p-3 rounded-full bg-white/5 backdrop-blur-md border border-white/10 shadow-xl hover:bg-white/10 transition-colors"
                >
                    {isDarkMode ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-blue-600" />}
                </motion.button>
                <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowPreferences(!showPreferences)}
                    className="p-3 rounded-full bg-white/5 backdrop-blur-md border border-white/10 shadow-xl hover:bg-white/10 transition-colors"
                >
                    <Settings size={20} className={isDarkMode ? 'text-white' : 'text-slate-900'} />
                </motion.button>
            </motion.div>

            {/* Preferences Panel */}
            <AnimatePresence>
                {showPreferences && (
                    <motion.div
                        className="absolute right-6 top-20 z-50"
                        initial={{ opacity: 0, scale: 0.9, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: -10 }}
                    >
                        {/* Shortened for brevity - keeping logic same */}
                        <div className={`p-4 rounded-2xl shadow-2xl backdrop-blur-xl border w-64 ${isDarkMode ? 'bg-slate-900/90 border-white/10' : 'bg-white/90 border-slate-200'}`}>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('settings.title', 'Ayarlar')}</h3>
                                <button onClick={() => setShowPreferences(false)}><X size={18} className={isDarkMode ? 'text-white' : 'text-slate-900'} /></button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className={`text-xs font-semibold uppercase tracking-wider mb-2 block ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('settings.language', 'Dil')}</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {SUPPORTED_LANGUAGES.map((lang) => (
                                            <button
                                                key={lang.code}
                                                onClick={() => {
                                                    triggerLanguageChange(lang.code);
                                                    setSelectedLanguage(lang.code);
                                                }}
                                                className={`px-3 py-2 rounded-lg text-sm transition-all ${selectedLanguage === lang.code
                                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                                                    : isDarkMode ? 'bg-white/5 text-gray-300 hover:bg-white/10' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                    }`}
                                            >
                                                <span className="mr-2">{lang.flag}</span>
                                                {lang.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* CENTRAL CONTENT WRAPPER */}
            <div className="flex flex-col items-center justify-center w-full max-w-lg z-20 space-y-12">

                {/* LOGO SECTION */}
                <div className={`w-full flex items-center justify-center relative transition-all duration-700 ${isExiting ? 'z-50' : 'z-20'}`}>
                    <motion.div
                        className="relative"
                        variants={isExiting ? {} : logoGlowVariants}
                        animate={isExiting ? {
                            y: -window.innerHeight * 0.35,
                            scale: 0.4,
                            transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
                        } : "animate"}
                    >
                        <div
                            className={!isExiting ? "animate-float" : ""}
                            style={!isExiting ? { animation: 'float 6s ease-in-out infinite' } : {}}
                        >
                            <motion.div
                                variants={logoGlowVariants}
                                animate="animate"
                                className={`absolute inset-0 rounded-full blur-[60px] ${isDarkMode ? 'bg-blue-600/40' : 'bg-blue-400/30'}`}
                            />
                            <motion.img
                                src={Logo}
                                alt="IDApps Logo"
                                className="w-40 h-40 object-contain drop-shadow-2xl relative z-10"
                                style={{ filter: isDarkMode ? 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.5))' : 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.3))' }}
                            />
                        </div>
                    </motion.div>
                </div>

                {/* CONTENT SECTION */}
                <motion.div
                    className="w-full flex flex-col items-center px-8"
                    animate={{ opacity: isExiting ? 0 : 1, y: isExiting ? 20 : 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="text-center mb-10 space-y-4">
                        <div className={`${isDarkMode ? 'text-white drop-shadow-md' : 'text-slate-900'} text-3xl md:text-5xl font-bold tracking-tight text-center`}>
                            <DecryptedText
                                text={t('welcome.heroTitle', 'Your Identity, Reimagined.')}
                                speed={60}
                                maxIterations={20}
                                sequential={true}
                                revealDirection="center"
                                animateOn="view"
                                className="font-bold tracking-tight"
                                encryptedClassName="opacity-30"
                            />
                        </div>

                        <div className="h-6 overflow-hidden flex items-center justify-center">
                            <TypingText
                                text={slogans}
                                typingSpeed={50}
                                pauseDuration={2000}
                                deletingSpeed={30}
                                showCursor={true}
                                cursorCharacter="|"
                                className={`text-sm md:text-base font-medium ${isDarkMode ? 'text-blue-100/90' : 'text-slate-600'}`}
                                textColors={isDarkMode
                                    ? ['#60a5fa', '#a78bfa', '#2dd4bf']
                                    : ['#2563eb', '#7c3aed', '#0d9488']
                                }
                                variableSpeed={{ min: 40, max: 80 }}
                            />
                        </div>
                    </div>

                    <div className="w-full max-w-xs space-y-4">
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleGetStarted}
                            className={`w-full py-4 rounded-full font-bold text-lg shadow-lg flex items-center justify-center gap-2 relative overflow-hidden group ${isDarkMode
                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-500/20 hover:shadow-blue-500/40'
                                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200'
                                }`}
                        >
                            <span>{t('welcome.getStarted', 'Get Started')}</span>
                        </motion.button>

                        {isMobile && (
                            <motion.button
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`w-full py-4 rounded-full font-bold text-lg border flex items-center justify-center gap-2 transition-colors ${isDarkMode
                                    ? 'bg-white text-black border-transparent hover:bg-gray-200'
                                    : 'bg-slate-900 text-white border-transparent hover:bg-slate-800'
                                    }`}
                            >
                                <Apple size={22} className="mb-0.5" />
                                <span>{t('welcome.signInWithApple', 'Sign in with Apple')}</span>
                            </motion.button>
                        )}

                        <div className="pt-2 text-center">
                            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-slate-500'} mr-2`}>
                                {t('welcome.haveAccount', 'Already have an account?')}
                            </span>
                            <button
                                onClick={() => navigateToScreen(SCREENS.LOGIN)}
                                className={`text-sm font-semibold transition-colors ${isDarkMode ? 'text-white hover:text-blue-300' : 'text-slate-900 hover:text-blue-600'
                                    }`}
                            >
                                {t('welcome.login', 'Log In')}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Footer Info - Absolute Bottom */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className={`absolute bottom-6 text-[10px] uppercase tracking-widest opacity-30 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}
            >
                IDAPPS OS v2.1
            </motion.div>
        </motion.div>
    );
};

export default WelcomeScreen;